/**
 * Where a browser keeps the messages it has decrypted (ADR-0027 D4.6).
 *
 * ## Why this has to exist at all
 *
 * Reading the inbox is only half an operation: the other half is the **ack**,
 * and an ack DELETES the ciphertext from the server (`ack_encrypted_messages`
 * purges the row, by design — the relay keeps nothing it need not relay). After
 * that nobody in the world can re-serve that message to this browser. Our own
 * server holds no plaintext, and it cannot re-encrypt what it cannot read.
 *
 * So a decrypted message that lives only in `store.state.messages` — which
 * holds ONE chat and dies with the tab — is a message destroyed by the act of
 * receiving it. Switching chats would lose it. A reload would lose it.
 * Persistence is not a nicety bolted onto step 5; it is what makes the ack
 * safe, and it is why this file is part of the same change.
 *
 * The phones already had this and it was invisible: their plaintext lands in
 * the SQLCipher cache before the ack goes out (`ChatService.persistDecrypted`).
 * This is the browser's equivalent, with the browser's honest limits.
 *
 * ## At rest
 *
 * Encrypted under an **AES-GCM key WebCrypto created as non-extractable**, the
 * same discipline and the same limit as `device-vault.ts`: script can decrypt
 * in place, so an XSS reads messages; script cannot export the key and carry
 * the archive away. A fresh IV per write, always — reusing one under a single
 * key is a total break, not a weakness.
 *
 * The key is this cache's OWN, not the vault's. Two reasons: the vault lives in
 * the signal worker and this lives in the shell (the worker holds keys, the
 * shell holds messages, and plaintext already crosses that boundary by
 * design), and a cache that could reach into the vault's storage would be a
 * second path to the device record.
 *
 * ## One account
 *
 * `open` takes the account this browser is signed into. A cache holding
 * somebody else's messages is DROPPED rather than merely refused — the vault
 * can afford to refuse, because its single device record is overwritten by the
 * next `save`, but nothing here would ever overwrite an old account's chats.
 * Refusing alone would leave them on disk forever, growing, on a computer that
 * has demonstrably been shared.
 */

/** The tiny slice of a key/value store this needs — IndexedDB in the browser,
 *  a Map in a test. Deliberately the same shape as the vault's, and
 *  deliberately not the same STORE. */
export interface CacheStorage {
  get(key: string): Promise<unknown>;
  put(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
  /** Every key currently held. Used to enumerate a chat's messages and to drop
   *  a previous account's. */
  keys(): Promise<string[]>;
}

const WRAP_KEY = "cache-wrap-key";
const OWNER = "cache-owner";
const IV_BYTES = 12;
const MESSAGE_PREFIX = "m:";

/** One decrypted message, as this browser will render it. Field names mirror
 *  the shell's `Message` because that is where every one of these is going;
 *  the mapping happens once, at the drain, not on every read. */
export interface CachedMessage {
  /** The sender's `client_msg_id` — the id BOTH ends key an E2E message by.
   *  There is no server message row to have an id of. */
  clientMsgId: string;
  chatId: string;
  senderUserId: string;
  body: string;
  createdAt: string;
  /**
   * What kind of message this is — `text` unless something says otherwise.
   *
   * Carried rather than derived, because a history bundle (ADR-0027 D4.7) is
   * the first thing that can hand this browser an image or a voice note: a
   * LIVE encrypted message is always text or a sticker today (encrypted media
   * is not built), but the messages a phone re-sends were written before the
   * chat was flagged and can be anything.
   */
  type?: string;
  /** Media BY REFERENCE. The bytes stay on the server and are fetched with the
   *  same bearer every other surface uses; copying them through a per-device
   *  ratchet would multiply an album by the number of linked devices. */
  mediaUrl?: string | null;
  thumbUrl?: string | null;
  /** Set when the envelope could not be read and this is the tombstone the
   *  reader sees instead of silence. */
  undecryptable?: boolean;
  replyToId?: string | null;
  forwarded?: boolean;
  stickerRef?: string | null;
  /** A11 — the wall-clock this message disappears at, computed from when the
   *  SENDER sent it. A reader who was offline for two days does not get a
   *  fresh 24 hours. */
  expiresAt?: number | null;
}

interface StoredMessage {
  chatId: string;
  iv: Uint8Array;
  ciphertext: Uint8Array;
}

export class MessageCache {
  private readonly storage: CacheStorage;
  private readonly subtle: SubtleCrypto;
  private readonly randomBytes: (n: number) => Uint8Array;

  private constructor(storage: CacheStorage, crypto: Crypto) {
    this.storage = storage;
    this.subtle = crypto.subtle;
    this.randomBytes = (n) => crypto.getRandomValues(new Uint8Array(n));
  }

  /**
   * Open the cache for one account, dropping anything a different account left
   * behind. Always the entry point — a constructor that skipped this would be
   * one call away from serving the last person's chats to this one.
   */
  static async open(
    storage: CacheStorage,
    userId: string,
    crypto: Crypto = globalThis.crypto,
  ): Promise<MessageCache> {
    const cache = new MessageCache(storage, crypto);
    const owner = (await storage.get(OWNER)) as string | undefined;
    if (owner !== userId) {
      await cache.forget();
      await storage.put(OWNER, userId);
    }
    return cache;
  }

  private async wrapKey(): Promise<CryptoKey> {
    const existing = await this.storage.get(WRAP_KEY);
    if (existing) return existing as CryptoKey;
    const key = await this.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, [
      "encrypt",
      "decrypt",
    ]);
    await this.storage.put(WRAP_KEY, key);
    return key;
  }

  /**
   * Store one decrypted message.
   *
   * Keyed by `client_msg_id` so a redelivery — which store-and-forward makes
   * ordinary, since an unacked envelope comes back on the next drain —
   * overwrites rather than duplicates.
   */
  async put(message: CachedMessage): Promise<void> {
    const key = await this.wrapKey();
    const iv = this.randomBytes(IV_BYTES);
    const plain = new TextEncoder().encode(JSON.stringify(message));
    const ciphertext = new Uint8Array(
      await this.subtle.encrypt({ name: "AES-GCM", iv }, key, plain),
    );
    // The chat id rides in the CLEAR, and only the chat id. Enumerating one
    // chat otherwise means decrypting every message this browser holds on
    // every open, which is a per-chat cost that grows with the whole archive.
    // What it discloses to someone already reading this database is which
    // conversations exist — and they can read the bodies anyway.
    const stored: StoredMessage = { chatId: message.chatId, iv, ciphertext };
    await this.storage.put(MESSAGE_PREFIX + message.clientMsgId, stored);
  }

  /**
   * Everything this browser has decrypted for one chat, oldest first.
   *
   * **Expired messages are deleted here, not merely hidden.** A11 says the
   * sender's timer is enforced by the reader, and a browser that kept the
   * plaintext of a disappearing message in IndexedDB forever while declining to
   * draw it would be breaking that promise in the one place nobody can see. The
   * deadline is the one computed from when the SENDER sent, so a reader who was
   * away for two days does not get a fresh timer.
   */
  async forChat(chatId: string, now: number = Date.now()): Promise<CachedMessage[]> {
    const key = await this.wrapKey();
    const out: CachedMessage[] = [];
    for (const k of await this.storage.keys()) {
      if (!k.startsWith(MESSAGE_PREFIX)) continue;
      const raw = (await this.storage.get(k)) as StoredMessage | undefined;
      if (!raw || raw.chatId !== chatId) continue;
      let plain: ArrayBuffer;
      try {
        plain = await this.subtle.decrypt({ name: "AES-GCM", iv: raw.iv }, key, raw.ciphertext);
      } catch {
        // One unreadable record must not cost the whole conversation. It is
        // skipped, not thrown: the alternative is a chat that renders nothing
        // because of a single corrupt row.
        continue;
      }
      let message: CachedMessage;
      try {
        message = JSON.parse(new TextDecoder().decode(plain)) as CachedMessage;
      } catch {
        continue;
      }
      if (message.expiresAt != null && message.expiresAt <= now) {
        await this.storage.delete(k);
        continue;
      }
      out.push(message);
    }
    return out.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  /** Drop one message — A10's delete-for-everyone, applied to what this
   *  browser already read. */
  async remove(clientMsgId: string): Promise<void> {
    await this.storage.delete(MESSAGE_PREFIX + clientMsgId);
  }

  /**
   * Destroy everything, including the key.
   *
   * Called when this browser stops being a device: sign-out, unlink, or a
   * different account opening the cache. The key goes with the messages for
   * the same reason the vault's does — leaving a key that opens nothing is
   * harmless but dishonest, and the UI promises that unlinking takes the chats
   * with it.
   */
  async forget(): Promise<void> {
    for (const k of await this.storage.keys()) await this.storage.delete(k);
  }
}
